import cs from "classnames";
import React from "react";

import { useTrackFocusState } from "src/common/hooks/useTrackFocusState";

import "./Slider.css";

const defaultOptions: Option[] = [];
const noop = (): void => {};

interface SliderProps {
	disabled?: boolean;
	options?: Option[];
	value?: string | undefined;
	onChangeValue?(value: string | undefined): unknown;
	mapValueToColor?(value: string | undefined): string | undefined;
}

export interface Option {
	value: string;
	label: React.ReactNode;
}

export const Slider: React.FunctionComponent<SliderProps> = ({
	disabled,
	options = defaultOptions,
	value,
	onChangeValue = noop,
	mapValueToColor
}) => {
	const [isFocused, onFocus, onBlur] = useTrackFocusState();

	const index = React.useMemo(
		() =>
			Math.max(
				options.findIndex(option => option.value === value),
				0
			),
		[options, value]
	);

	const dynamicStyle = React.useMemo(
		() =>
			mapValueToColor ? { backgroundColor: mapValueToColor(value) } : {},
		[mapValueToColor, value]
	);

	const optionsRef = React.useRef(options);
	const isMouseDownRef = React.useRef(false);
	const inputRef = React.useRef<HTMLDivElement>(null);

	const onMouseMove = React.useCallback(
		(event: React.MouseEvent<Element> | MouseEvent) => {
			const { current: options } = optionsRef;

			const { current: input } = inputRef;
			if (input === null) {
				return;
			}

			const { clientX } = event;
			const { x = 0, width } = input.getBoundingClientRect();

			const relativeClientX = clientX - x;
			const stepCount = options.length - 1;
			const stepSize = width / stepCount;
			const stepIndex =
				stepSize === 0
					? 0
					: Math.min(
							Math.floor(
								(relativeClientX + stepSize / 2) / stepSize
							),
							stepCount
						);

			const { value } = options[stepIndex];
			onChangeValue(value);
		},
		[onChangeValue]
	);

	const onMouseDown = React.useCallback(
		(event: React.MouseEvent<Element>) => {
			isMouseDownRef.current = true;
			onMouseMove(event);
			onFocus();
			window.addEventListener("mousemove", onMouseMove);
		},
		[onMouseMove, onFocus]
	);

	const onMouseUp = React.useCallback(() => {
		if (isMouseDownRef.current) {
			isMouseDownRef.current = false;
			onBlur();
			window.removeEventListener("mousemove", onMouseMove);
		}
	}, [onMouseMove, onBlur]);

	React.useEffect(() => {
		window.addEventListener("mouseup", onMouseUp);
		return () => {
			window.removeEventListener("mouseup", onMouseUp);
		};
	});

	return (
		<div
			ref={inputRef}
			role="slider"
			aria-valuemin={0}
			aria-valuemax={options.length - 1}
			aria-valuenow={index}
			aria-valuetext={value}
			className={cs("slider", {
				"slider--focused": isFocused,
				"slider--disabled": disabled
			})}
			tabIndex={disabled ? -1 : 0}
			onFocus={onFocus}
			onBlur={onBlur}
			onMouseDown={onMouseDown}
			onKeyDown={event => {
				if (event.key === "ArrowLeft") {
					if (index > 0) {
						onChangeValue(options[index - 1].value);
						onFocus();
					}
				} else if (event.key === "ArrowRight") {
					if (index < options.length - 1) {
						onChangeValue(options[index + 1].value);
						onFocus();
					}
				}
			}}
		>
			<div className="slider__container slider__values">
				{options.map(({ value, label }, index) => (
					<div
						key={value}
						className="slider__value"
						style={{
							top: 0,
							left: getLeftPosByIndex(options, index)
						}}
					>
						{label}
					</div>
				))}
			</div>
			<div className="slider__container slider__ticks">
				{options.map(({ value }, index) => (
					<div
						key={value}
						className="slider__tick"
						style={{ left: getLeftPosByIndex(options, index) }}
					/>
				))}
			</div>
			<div className="slider__rail" style={dynamicStyle}>
				<div className="slider__container">
					<div
						className="slider__thumb"
						style={{ left: getLeftPosByIndex(options, index) }}
					>
						<div className="slider__ripple"></div>
						<div className="slider__circle" style={dynamicStyle} />
					</div>
				</div>
			</div>
		</div>
	);
};

function getLeftPosByIndex({ length }: any[], index: number): string {
	return `${(index / (length - 1)) * 100}%`;
}
